import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  Button,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";
import GetAppIcon from "@material-ui/icons/GetApp";
import TimerIcon from "@material-ui/icons/Timer";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const AUTO_REFRESH_MS = 5000;
const LEVEL_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "trace", label: "Trace e acima" },
  { value: "debug", label: "Debug e acima" },
  { value: "info", label: "Info e acima" },
  { value: "warn", label: "Warn e acima" },
  { value: "error", label: "Error e acima" },
];

const LEVEL_COLORS = {
  trace: "#94a3b8",
  debug: "#38bdf8",
  info: "#4ade80",
  warn: "#fbbf24",
  error: "#f87171",
  fatal: "#f43f5e",
  unknown: "#cbd5e1",
};

const formatBytes = bytes => {
  const value = Number(bytes || 0);
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const formatTime = time => {
  const date = new Date(Number(time) || Date.now());
  if (Number.isNaN(date.getTime())) return "--:--:--";
  return date.toLocaleTimeString("pt-BR", { hour12: false });
};

const useStyles = makeStyles(theme => ({
  filtersPaper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    padding: theme.spacing(1.5, 2),
    borderRadius: 14,
    marginBottom: theme.spacing(1.5),
  },
  levelField: { minWidth: 190 },
  searchField: { flex: 1, minWidth: 240 },
  spacer: { flex: 1 },
  console: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: theme.spacing(1.5),
    borderRadius: 14,
    backgroundColor: "#0b1220",
    fontFamily: "Consolas, 'Courier New', monospace",
    fontSize: "0.78rem",
    lineHeight: 1.55,
    ...theme.scrollbarStyles,
  },
  line: {
    display: "flex",
    gap: 10,
    padding: "1px 0",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  lineTime: { color: "#64748b", flexShrink: 0 },
  lineLevel: { fontWeight: 700, flexShrink: 0, width: 52 },
  lineText: { color: "#e2e8f0", minWidth: 0 },
  emptyState: {
    color: "#94a3b8",
    textAlign: "center",
    padding: theme.spacing(4),
  },
  statsBar: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    color: theme.palette.text.secondary,
    fontSize: "0.78rem",
    padding: theme.spacing(1, 0.5, 0),
  },
}));

const DebugLogs = () => {
  const classes = useStyles();

  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [levels, setLevels] = useState({ app: "", baileys: "", provider: "" });
  const [level, setLevel] = useState("");
  const [search, setSearch] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loading, setLoading] = useState(false);

  const consoleRef = useRef(null);
  const pinnedToBottomRef = useRef(true);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/debug-logs", {
        params: { level: level || undefined, search: search || undefined, limit: 500 },
      });
      setEntries(Array.isArray(data?.entries) ? data.entries : []);
      setStats(data?.stats || null);
      setLevels({
        app: data?.appLogLevel || "",
        baileys: data?.baileysLogLevel || "",
        provider: data?.activeProvider || "",
      });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [level, search]);

  useEffect(() => {
    const timeout = setTimeout(fetchLogs, 400);
    return () => clearTimeout(timeout);
  }, [fetchLogs]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const interval = setInterval(fetchLogs, AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchLogs]);

  // So rola sozinho se o usuario ja estava no fim: caso contrario ele perderia
  // a linha que esta lendo a cada atualizacao.
  useEffect(() => {
    const node = consoleRef.current;
    if (node && pinnedToBottomRef.current) {
      node.scrollTop = node.scrollHeight;
    }
  }, [entries]);

  const handleScroll = event => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    pinnedToBottomRef.current = scrollHeight - (scrollTop + clientHeight) < 40;
  };

  // Por padrao o buffer guarda so warn+ para nao pesar no servidor. Este botao
  // arma a captura completa por 10 minutos - ela desarma sozinha no backend.
  const handleArmVerbose = async () => {
    try {
      const { data } = await api.post("/debug-logs/verbose", { minutes: 10 });
      if (data?.stats) setStats(data.stats);
      fetchLogs();
    } catch (err) {
      toastError(err);
    }
  };

  const verboseActive = Boolean(
    stats?.verboseUntil && stats.verboseUntil > Date.now()
  );

  const handleExport = () => {
    const content = entries.map(entry => entry.raw).join("\n");
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `altchat-logs-${new Date().toISOString().slice(0, 19)}.log`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Logs do sistema</Title>
        <MainHeaderButtonsWrapper>
          <FormControlLabel
            label="Atualizar"
            labelPlacement="start"
            control={
              <Switch
                size="small"
                color="primary"
                checked={autoRefresh}
                onChange={event => setAutoRefresh(event.target.checked)}
              />
            }
          />
          <Button
            size="small"
            variant={verboseActive ? "contained" : "outlined"}
            color="primary"
            startIcon={<TimerIcon />}
            onClick={handleArmVerbose}
          >
            {verboseActive
              ? `Detalhado até ${formatTime(stats.verboseUntil)}`
              : "Detalhar (10 min)"}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={fetchLogs}
            disabled={loading}
          >
            Recarregar
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<GetAppIcon />}
            onClick={handleExport}
            disabled={!entries.length}
          >
            Exportar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper variant="outlined" className={classes.filtersPaper}>
        <TextField
          select
          size="small"
          variant="outlined"
          label="Nível"
          className={classes.levelField}
          value={level}
          onChange={event => setLevel(event.target.value)}
        >
          {LEVEL_OPTIONS.map(option => (
            <MenuItem key={option.value || "all"} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Filtrar por texto (ex.: baileys, connection, error)"
          className={classes.searchField}
          value={search}
          onChange={event => setSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Paper
        variant="outlined"
        className={classes.console}
        ref={consoleRef}
        onScroll={handleScroll}
      >
        {entries.length ? (
          entries.map(entry => (
            <div className={classes.line} key={entry.id}>
              <span className={classes.lineTime}>{formatTime(entry.time)}</span>
              <span
                className={classes.lineLevel}
                style={{ color: LEVEL_COLORS[entry.level] || LEVEL_COLORS.unknown }}
              >
                {String(entry.level || "").toUpperCase()}
              </span>
              <span className={classes.lineText}>{entry.raw}</span>
            </div>
          ))
        ) : (
          <Typography className={classes.emptyState}>
            {loading
              ? "Carregando..."
              : verboseActive
              ? "Nenhum registro no buffer para os filtros atuais."
              : "Nenhum registro. A captura padrão guarda só warn e acima — num servidor saudável isso é bom sinal. Use “Detalhar (10 min)” para capturar tudo."}
          </Typography>
        )}
      </Paper>

      {stats ? (
        <div className={classes.statsBar}>
          <span>
            {stats.entries} de {stats.maxEntries} linhas · {formatBytes(stats.approxBytes)}
          </span>
          <span>Nível da aplicação: {levels.app}</span>
          <span>Nível do Baileys ({levels.provider}): {levels.baileys}</span>
          <span>
            {verboseActive
              ? `Captura detalhada até ${formatTime(stats.verboseUntil)}`
              : "Captura padrão: warn e acima"}
          </span>
          {stats.droppedByLimit > 0 ? (
            <span>{stats.droppedByLimit} linha(s) antiga(s) descartada(s)</span>
          ) : null}
          {stats.droppedByBurst > 0 ? (
            <span>{stats.droppedByBurst} linha(s) descartada(s) por rajada</span>
          ) : null}
        </div>
      ) : null}
    </MainContainer>
  );
};

export default DebugLogs;
